import { useEffect } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import type { LoadResource } from "@prequel/import-react";
import { useLoad } from "@prequel/import-react";
import { PageHeading, Spinner } from "@prequel/shared-components";

import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type LoadContext = {
  load: LoadResource;
};

export function useLoadContext(): LoadContext {
  return useOutletContext<LoadContext>();
}

const LoadPage = () => {
  const streamID = useRequiredParam("streamID");
  const loadID = useRequiredParam("loadID");
  const { addToast } = useToast();
  const { data: load, error, isLoading } = useLoad(loadID);

  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load job details." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!load) {
    return <Navigate to={`/import/streams/${streamID}`} replace />;
  }

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title="Load Job"
        onGoBack={() => navigate(`/import/streams/${streamID}`)}
        breadcrumbs={<Breadcrumbs />}
      />
      <div className="flex-1">
        <div className="relative">
          <div className="pt-0 pb-16">
            <Outlet context={{ load }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadPage;
